import React from "react";

function About() {
    return (
        <div className="pt-24 pb-16 text-black bg-yellow-600/50 min-h-screen">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-600 to-yellow-900 bg-clip-text text-transparent">
                        About Semperor
                    </h1>
                    <p className="text-black text-lg">Established 2023 • Mobile Game Studio</p>
                </div>

                {/* Introduction */}
                <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-2xl p-6 md:p-8 mb-8 shadow-lg border border-yellow-900">
                    <p className="text-black leading-relaxed text-lg mb-4">
                        Founded in <span className="font-semibold text-black">2023</span>, Semperor Game Studio set out with a clear mission:
                        to create mobile gaming experiences that go beyond pure entertainment — experiences that engage the player's emotions,
                        imagination, and intellect, bridging technology, art, and human storytelling.
                    </p>
                    <p className="text-black leading-relaxed text-lg">
                        At Semperor, we see games not merely as products, but as a <span className="font-semibold text-black">powerful medium of expression</span> —
                        one that can inspire, connect, and open new worlds to players. That's why our focus has always been on high-quality design,
                        innovation in gameplay and narrative, and a deep understanding of our audience.
                    </p>
                </div>

                {/* Story Section */}
                <section className="mb-8 bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-6 md:p-8 shadow-lg border border-yellow-900">
                    <h2 className="text-2xl font-bold mb-4 text-black">Our Journey</h2>
                    <p className="text-black leading-relaxed mb-4">
                        Since its founding, Semperor has built a <span className="font-semibold text-black">strong reputation by developing 
                        and publishing</span> successful titles enjoyed by millions of players across different countries. Our passionate and skilled team 
                        continuously strives to create experiences that resonate with diverse audiences, keeping pace with the latest global trends 
                        in the gaming industry.
                    </p>
                    <p className="text-black leading-relaxed">
                        Collaboration with fresh talent and creative teams is one of our core values. We believe the future of game development
                        will be shaped through cooperation, education, and empowering the next generation. For this reason, Semperor has always acted
                        as a <span className="font-semibold text-black">bridge between developers, artists, and publishers</span> — building a sustainable
                        foundation for the growth of the gaming ecosystem.
                    </p>
                </section>

                {/* Values Section */}
                <section className="mb-8">
                    <h2 className="text-3xl font-bold mb-6 text-center text-black">Our Values & Beliefs</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Value 1 */}
                        <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-6 shadow-lg border border-yellow-900 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🎮</div>
                                <h3 className="text-xl font-bold text-black">Creativity & Innovation</h3>
                            </div>
                            <p className="text-black leading-relaxed">
                                We seek fresh ideas that push the boundaries of gameplay and continuously surprise and delight players.
                            </p>
                        </div>

                        {/* Value 2 */}
                        <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-6 shadow-lg border border-yellow-900 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🌍</div>
                                <h3 className="text-xl font-bold text-black">Quality & Authenticity</h3>
                            </div>
                            <p className="text-black leading-relaxed">
                                We believe every detail — from visual design to sound — shapes the overall experience and deserves to be crafted with excellence.
                            </p>
                        </div>

                        {/* Value 3 */}
                        <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-6 shadow-lg border border-yellow-900 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🤝</div>
                                <h3 className="text-xl font-bold text-black">Collaboration & Learning</h3>
                            </div>
                            <p className="text-black leading-relaxed">
                                We foster a dynamic, growth-oriented environment where individuals can learn, evolve, and achieve greater goals together.
                            </p>
                        </div>

                        {/* Value 4 */}
                        <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-6 shadow-lg border border-yellow-900 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🚀</div>
                                <h3 className="text-xl font-bold text-black">Sustainability & Growth</h3>
                            </div>
                            <p className="text-black leading-relaxed">
                                Semperor is future-focused — building a sustainable infrastructure for game development and a strong presence in the global market.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Vision Section */}
                <section className="mb-8 bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-6 md:p-8 shadow-lg border border-blue-500/30">
                    <h2 className="text-3xl font-bold mb-6 text-center text-black">Our Vision</h2>
                    <div className="space-y-4 text-black">
                        <p className="text-lg leading-relaxed">
                            Our vision is to become the <span className="font-semibold text-black">leading platform for mobile game development and publishing
                                in the Middle East</span>, empowering local and regional talents to create games that shine on the global stage.
                        </p>
                        <p className="text-lg leading-relaxed">
                            We aim to play an active role in shaping the future of the digital entertainment industry — a future where games are not only
                            a form of fun, but a <span className="font-semibold text-black">platform for learning, connection, identity, and social transformation</span>.
                        </p>
                        <p className="text-lg leading-relaxed font-semibold text-center mt-6">
                            At Semperor, we move toward that future step by step — with conviction, creativity, and boundless passion.
                        </p>
                    </div>
                </section>

                {/* Mission Statement */}
                <div className="mt-12 bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-2xl p-8 shadow-lg border border-blue-500/20 text-center">
                    <h2 className="text-2xl font-bold mb-4 text-black">Our Promise</h2>
                    <p className="text-xl text-black leading-relaxed max-w-3xl mx-auto italic">
                        "We believe in a future where games will play a more influential role than ever — in education, culture,
                        social interaction, and even major decision-making. And Semperor stands at the heart of that journey —
                        <span className="font-semibold text-black"> where dreams turn into games</span>."
                    </p>
                </div>

                {/* Stats/Highlights */}
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-4 text-center border border-yellow-900">
                        <div className="text-3xl font-bold text-black mb-1">+2</div>
                        <div className="text-sm text-black">Years of Excellence</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-4 text-center border border-yellow-900">
                        <div className="text-3xl font-bold text-black mb-1">Millions</div>
                        <div className="text-sm text-black">Players Worldwide</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-4 text-center border border-yellow-900">
                        <div className="text-3xl font-bold text-black mb-1">Global</div>
                        <div className="text-sm text-black">Market Presence</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 rounded-xl p-4 text-center border border-yellow-900">
                        <div className="text-3xl font-bold text-black mb-1">Innovation</div>
                        <div className="text-sm text-black">Driven Studio</div>
                    </div>
                </div>

                {/* CTA */}
                <div className="mt-12 text-center">
                    <p className="text-black mb-4">Want to join us on this journey?</p>
                    <a
                        href="/contact"
                        className="inline-block px-6 py-3 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition text-black font-semibold"
                    >
                        Get in Touch
                    </a>
                </div>
            </div>
        </div>
    );
}

export default About;